/**
 * LayersPanel — Layer management in the side panel
 */
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Visibility,
  VisibilityOff,
  MoreVert,
  DragIndicator,
  Layers,
  RadioButtonUnchecked,
  Timeline,
  Pentagon,
  Edit,
  Delete,
  ContentCopy,
  FileUpload,
  FileDownload,
  Search,
  Tune,
} from '@mui/icons-material';
import { tokens } from '../../../theme';
import { useGeoveraTheme } from '../../../theme/ThemeContext';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import {
  toggleLayerVisibility,
  setActiveLayer,
  deleteLayerAsync,
  duplicateLayerAsync,
  createLayerAsync,
  addLayer,
  removeLayer,
  type Layer,
} from '../../../store/slices/layersSlice';
import { setCreateLayerDialogOpen } from '../../../store/slices/digitizationSlice';
import { CreateLayerDialog } from '../../CreateLayerDialog';
import LayerPropertiesDialog from '../../LayerPropertiesDialog';
import type { CreateLayerConfig } from '../../../types/layerCreation';
import { addNotification } from '../../../store/slices/uiSlice';

const GeomIcon: React.FC<{ type?: string }> = ({ type }) => {
  const { palette } = useGeoveraTheme();
  const color = palette.text.muted;
  if (type === 'Point') return <RadioButtonUnchecked sx={{ fontSize: 14, color }} />;
  if (type === 'LineString') return <Timeline sx={{ fontSize: 14, color }} />;
  return <Pentagon sx={{ fontSize: 14, color }} />;
};

const LayersPanel: React.FC = () => {
  const { palette } = useGeoveraTheme();
  const dispatch = useAppDispatch();
  const layers = useAppSelector((s) => s.layers.layers);
  const activeLayerId = useAppSelector((s) => s.layers.activeLayerId);
  const createDialogOpen = useAppSelector((s) => s.digitization.createLayerDialogOpen);

  const [search, setSearch] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuLayerId, setMenuLayerId] = useState<string | null>(null);

  // Layer Properties Dialog state
  const [propLayer, setPropLayer] = useState<Layer | null>(null);

  const filtered = layers.filter((l) => l.name.toLowerCase().includes(search.toLowerCase()));

  const handleMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>, id: string) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuLayerId(id);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setMenuLayerId(null);
  }, []);

  const handleDelete = useCallback(async () => {
    if (menuLayerId) {
      try {
        await dispatch(deleteLayerAsync(menuLayerId)).unwrap();
      } catch {
        dispatch(removeLayer(menuLayerId));
      }
    }
    handleMenuClose();
  }, [menuLayerId, dispatch, handleMenuClose]);

  const handleDuplicate = useCallback(() => {
    if (menuLayerId) dispatch(duplicateLayerAsync(menuLayerId));
    handleMenuClose();
  }, [menuLayerId, dispatch, handleMenuClose]);

  const handleOpenPropertiesFromMenu = useCallback(() => {
    if (menuLayerId) {
      const target = layers.find((l) => l.id === menuLayerId) || null;
      setPropLayer(target);
    }
    handleMenuClose();
  }, [menuLayerId, layers, handleMenuClose]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ─── Toolbar ─────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.75,
          borderBottom: `1px solid ${palette.border.subtle}`,
        }}
      >
        <TextField
          size="small"
          placeholder="Filter layers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 14, color: palette.text.muted }} />
                </InputAdornment>
              ),
              sx: { fontSize: tokens.fontSize.sm, height: 28 },
            },
          }}
        />
        <Tooltip title="Add layer" arrow>
          <IconButton
            size="small"
            onClick={() => dispatch(setCreateLayerDialogOpen(true))}
            sx={{
              width: 28,
              height: 28,
              color: palette.accent.primary,
              '&:hover': { backgroundColor: palette.accent.primaryDim },
            }}
          >
            <Add sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Import layer (file / data source)" arrow>
          <IconButton
            size="small"
            onClick={() => window.dispatchEvent(new CustomEvent('geovera:open-import'))}
            sx={{
              width: 28,
              height: 28,
              color: palette.text.muted,
              '&:hover': { color: palette.text.primary },
            }}
          >
            <FileUpload sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ─── Layer list ──────────────────────────── */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 0.5 }}>
        {filtered.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              gap: 1.5,
              color: palette.text.muted,
            }}
          >
            <Layers sx={{ fontSize: 40, opacity: 0.3 }} />
            <Typography sx={{ fontSize: tokens.fontSize.sm, textAlign: 'center' }}>
              {search ? 'No layers match your filter' : 'No layers yet'}
            </Typography>
            {!search && (
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() => dispatch(setCreateLayerDialogOpen(true))}
                sx={{ fontSize: tokens.fontSize.sm }}
              >
                Add Layer
              </Button>
            )}
          </Box>
        ) : (
          <List dense disablePadding>
            {filtered.map((layer) => {
              const isActive = layer.id === activeLayerId;
              return (
                <ListItem
                  key={layer.id}
                  disablePadding
                  sx={{
                    '&:hover .layer-actions': { opacity: 1 },
                  }}
                >
                  <Tooltip
                    title="Double-click to open Layer Properties"
                    arrow
                    placement="top-start"
                  >
                    <ListItemButton
                      selected={isActive}
                      onClick={() => dispatch(setActiveLayer(layer.id))}
                      onDoubleClick={() => setPropLayer(layer)}
                      sx={{
                        py: 0.5,
                        px: 1,
                        gap: 0.75,
                        borderRadius: tokens.radius.sm,
                        mx: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: palette.accent.primaryDim,
                        },
                      }}
                    >
                      {/* Drag handle */}
                      <DragIndicator
                        sx={{
                          fontSize: 14,
                          color: palette.text.muted,
                          opacity: 0.4,
                          cursor: 'grab',
                          flexShrink: 0,
                        }}
                      />

                      {/* Geometry icon */}
                      <GeomIcon type={layer.geometryType} />

                      {/* Layer name */}
                      <ListItemText
                        primary={layer.name}
                        secondary={`${layer.features?.length ?? 0} features`}
                        primaryTypographyProps={{
                          sx: {
                            fontSize: tokens.fontSize.md,
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? palette.accent.primary : palette.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                        secondaryTypographyProps={{
                          sx: {
                            fontSize: tokens.fontSize.xs,
                            color: palette.text.muted,
                          },
                        }}
                      />

                      {/* Actions (visible on hover) */}
                      <Box
                        className="layer-actions"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                          opacity: 0,
                          transition: `opacity ${tokens.animation.fast}`,
                          flexShrink: 0,
                        }}
                      >
                        <Tooltip title={layer.visible ? 'Hide layer' : 'Show layer'} arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch(toggleLayerVisibility(layer.id));
                            }}
                            sx={{
                              width: 22,
                              height: 22,
                              color: layer.visible ? palette.text.secondary : palette.text.muted,
                            }}
                          >
                            {layer.visible ? (
                              <Visibility sx={{ fontSize: 14 }} />
                            ) : (
                              <VisibilityOff sx={{ fontSize: 14 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="More options" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, layer.id)}
                            sx={{ width: 22, height: 22, color: palette.text.muted }}
                          >
                            <MoreVert sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* ─── Footer stats ────────────────────────── */}
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          borderTop: `1px solid ${palette.border.subtle}`,
          display: 'flex',
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: tokens.fontSize.xs, color: palette.text.muted }}>
          {layers.length} {layers.length === 1 ? 'layer' : 'layers'}
        </Typography>
        <Typography sx={{ fontSize: tokens.fontSize.xs, color: palette.text.muted }}>·</Typography>
        <Typography sx={{ fontSize: tokens.fontSize.xs, color: palette.text.muted }}>
          {layers.filter((l) => l.visible).length} visible
        </Typography>
      </Box>

      {/* ─── Context menu ────────────────────────── */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { minWidth: 160 } }}
      >
        <MenuItem onClick={handleOpenPropertiesFromMenu} dense>
          <ListItemIcon>
            <Tune fontSize="small" />
          </ListItemIcon>
          <ListItemText>Properties...</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleDuplicate} dense>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleDelete} dense sx={{ color: palette.accent.error }}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: palette.accent.error }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* ─── Layer properties dialog ─────────────── */}
      <LayerPropertiesDialog
        open={Boolean(propLayer)}
        layer={propLayer}
        onClose={() => setPropLayer(null)}
      />

      {/* ─── Create layer dialog ─────────────────── */}
      <CreateLayerDialog
        open={createDialogOpen}
        onClose={() => dispatch(setCreateLayerDialogOpen(false))}
        onConfirm={async (config: CreateLayerConfig) => {
          try {
            await dispatch(
              createLayerAsync({
                name: config.name,
                geometryType: config.geometryType,
                crs: config.crs || 'EPSG:4326',
              })
            ).unwrap();
            dispatch(setCreateLayerDialogOpen(false));
            dispatch(
              addNotification({ type: 'success', message: `Layer “${config.name}” created` })
            );
          } catch {
            // Client-only fallback so Create always works
            const id = `local-${Date.now()}`;
            dispatch(
              addLayer({
                id,
                name: config.name,
                type: 'vector',
                geometryType: config.geometryType,
                visible: true,
                opacity: 1,
                zIndex: layers.length + 1,
                featureCount: 0,
                features: [],
                crs: config.crs || 'EPSG:4326',
              })
            );
            dispatch(setActiveLayer(id));
            dispatch(setCreateLayerDialogOpen(false));
            dispatch(
              addNotification({
                type: 'success',
                message: `Layer “${config.name}” created (local)`,
              })
            );
          }
        }}
      />
    </Box>
  );
};

export default LayersPanel;
